import { create } from 'zustand';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import _ from 'lodash';
import {
  getEngagementDetails,
  getQAFilters,
  getScoringFormWithRecording,
  postScoringForm,
  getFeedBackForm,
  getAllQaScoringAndFeedbackForm,
  getAllAgentAndQaList,
  getQaAuditDataForGraphDesign,
  postFeedBackForm,
  assignAgentsToQa,
  getLocationView,
  getLocationViewFilterData,
  getCoachView,
  getCoachViewFilterData,
  getQaView,
  getQaViewFilterData,
  getDisputeApply,
  getDisputeReject
} from '../api/api';
import moment from 'moment';
import { QA_FORM_FILLED_PURPOSE } from 'config';
import { isEmpty } from 'lodash';

export const useEngagementStore = create((set, get) => ({
  engagementData: {},
  engagementLoading: false,
  engagementError: null,
  filtersData: [],
  filtersLoading: false,
  filtersError: null,
  scoringFormData: null,
  scoringFormLoading: false,
  scoringFormError: null,
  coachUserId: null,
  callDetails: null,
  isFeedbackFormDisabled: false,
  feedbackForm: { feedBackFormResponse: [] },
  feedbackFormLoading: false,
  feedbackFormError: null,
  qaScoringFeedbackData: {},
  qaScoringFeedbackLoading: false,
  qaScoringFeedbackError: null,
  allAgentQaData: {},
  allAgentQaLoading: false,
  allAgentQaError: null,
  chartAuditData: null,
  chartAuditLoading: false,
  chartAuditError: null,
  locationViewData: {
    headers: [],
    total_row_count: 0,
    page_number: 0,
    page_size: 0,
    view_type: "",
    is_downloadable: false,
    rows_data: [],
    others: {},
  },
  locationViewDataLoading: false,
  locationViewDataError: null,
  locationViewFilterData: [],
  locationViewFilterDataLoading: false,
  locationViewFilterDataError: null,
  coachViewData: {
    headers: [],
    total_row_count: 0,
    page_number: 0,
    page_size: 0,
    view_type: "",
    is_downloadable: false,
    rows_data: [],
    others: {},
  },
  coachViewDataLoading: false,
  coachViewDataError: null,
  coachViewFilterData: [],
  coachViewFilterDataLoading: false,
  coachViewFilterDataError: null,
  qaViewData: {
    headers: [],
    total_row_count: 0,
    page_number: 0,
    page_size: 0,
    view_type: "",
    is_downloadable: false,
    rows_data: [],
    others: {},
  },
  qaViewDataLoading: false,
  qaViewDataError: null,
  qaViewFilterData: [],
  qaViewFilterDataLoading: false,
  qaViewFilterDataError: null,
  disputeApplyData: [],
  disputeApplyDataLoading: false,
  disputeApplyDataError: null,
  disputeRejecData: [],
  disputeRejectDataLoading: false,
  disputeRejectDataError: null,

  queryParams: { monthCount: 1, callDate: `[${dayjs().format('YYYY-MM-DD')},${dayjs().format('YYYY-MM-DD')}]`, pageNumber: 1, pageSize: 10 },
  setQueryParams: (params) => set((state) => ({
    queryParams: { ...state.queryParams, ...params }
  })),

  setIsFeedbackFormDisabled: (isFeedbackFormDisabled) => set(() => ({ isFeedbackFormDisabled })),

  setFeedbackForm: (newFeedbackForm) => set({ feedbackForm: newFeedbackForm }),

  fetchFeedbackForm: async (engagementTrackingId) => {
    try {
      set({ feedbackFormLoading: true, feedbackFormError: null });
      const response = await getFeedBackForm(engagementTrackingId);
      set({ feedbackForm: response.data });
      if (response.data?.feedBackFormResponse[0]?.respondAnswer?.length !== 0) {
        set({ isFeedbackFormDisabled: true });
      }
    } catch (error) {
      set({ feedbackFormError: error.message });
      toast.error(error?.response?.data?.message);
    } finally {
      set({ feedbackFormLoading: false });
    }
  },

  postFeedbackForm: async (setToaster, data) => {
    try {
      set({ feedbackFormLoading: true, feedbackFormError: null });
      const response = await postFeedBackForm(data);
      setToaster("Success!!!", response.data.message, "success");
    } catch (error) {
      set({ feedbackFormError: error.message });
      setToaster("Error!!!", error.response?.data?.message || "An error occurred", "danger");
    } finally {
      set({ feedbackFormLoading: false });
    }
  },

  fetchEngagementDetails: async (params = {}) => {
    const {
      agents = null,
      monthCount = 1,
      durationRangeInMinute = null,
      callDate = `[${dayjs().format('YYYY-MM-DD')},${dayjs().format('YYYY-MM-DD')}]`,
      pageNumber = 1,
      pageSize = 10,
      gender,
      engagementType,
    } = params;
    try {
      set({ engagementLoading: true, engagementError: null });
      const response = await getEngagementDetails({
        agents,
        monthCount,
        durationRangeInMinute,
        callDate,
        engagementType,
        gender,
        pageNumber,
        pageSize
      });
      const { agentDetails, paginationTotalCount, actualTotalCount } = response.data;
      set({ engagementData: { agentDetails, paginationTotalCount, actualTotalCount } });
    } catch (error) {
      set({ engagementError: error.message });
      toast.error(error.message);
    } finally {
      set({ engagementLoading: false });
    }
  },

  updateQueryParams: async (payload) => {
    try {
      const response = await getEngagementDetails(payload);
      const { agentDetails, paginationTotalCount, actualTotalCount } = response.data;
      set({ engagementData: { agentDetails, paginationTotalCount, actualTotalCount } });
    } catch (error) {
      console.error(error);
    }
  },

  fetchQAFilters: async () => {
    try {
      set({ filtersLoading: true });
      const response = await getQAFilters();
      set({ filtersData: response.data });
    } catch (error) {
      set({ filtersError: error.response?.data?.message || "An error occurred" });
      toast.error(error.response?.data?.message || "An error occurred", "danger");
    } finally {
      set({ filtersLoading: false });
    }
  },

  fetchScoringFormWithRecording: async (key,isIVRFormRequired,engagementTrackingId,coachUserId) => {
    try {
      set({ scoringFormLoading: true, scoringFormError: null });
      let formFilledPurpose = QA_FORM_FILLED_PURPOSE.FORM_AUDIT_BY_QA;
      if(key === "ata") {
        formFilledPurpose = QA_FORM_FILLED_PURPOSE.FORM_AUDIT_BY_QA_TL;
      }else if(key === "dispute") {
        formFilledPurpose = QA_FORM_FILLED_PURPOSE.FORM_AUDIT_BY_QA_TL_BUT_DISPUTED_BY_COACH;
      }
      const response = await getScoringFormWithRecording(formFilledPurpose,isIVRFormRequired,engagementTrackingId,coachUserId);
      const callDetails = {
        customerName: response.data.customerName,
        customerEmail: response.data.customerEmail,
        customerPhoneNumber: response.data.customerPhoneNumber,
        callService: response.data.callService,
        callType: response.data.callType,
        callDuration: response.data.callDuration,
        recordingUrl: response.data.recordingUrl,
        caseId: response.data.caseId,
      };
      set({ scoringFormData: response.data, callDetails, coachUserId: response.data.coachUserId });
    } catch (error) {
      set({ scoringFormError: error.message });
      toast.error(error?.response?.data?.message);
    } finally {
      set({ scoringFormLoading: false });
    }
  },

  submitScoringForm: async (payload,tabkey) => {
    try {
      const { fetchScoringFormWithRecording } = get()
      const response = await postScoringForm(payload);
      if (response.status === 200) {
        toast.success('Form Submitted Successfully');
        let key = tabkey || null;
        if(isEmpty(key)){
          key = "questions";
        }
        await fetchScoringFormWithRecording(key,payload.toggleState,payload.engagementTrackingId,payload.agentUserId);
      };
      return response.data;
    } catch (error) {
      toast.error(error.response.data.message);
    }
  },

  fetchAllAgentAndQaData: async () => {
    try {
      set({ allAgentQaLoading: true, allAgentQaError: null });
      const response = await getAllAgentAndQaList();
      set({ allAgentQaData: response.data });
    } catch (error) {
      set({ allAgentQaError: error.message });
      toast.error(error.message);
    } finally {
      set({ allAgentQaLoading: false });
    }
  },

  fetchQaScoringFeedbackData: async (params = {}) => {
    const {
      agentUserId = null,
      qaUserId = null,
      monthCount = null,
      engagementType = null,
      gender = null,
      verdict = null,
      house = null,
      location = null,
      auditedDate = `[${moment().format('YYYY-MM-DD')},${moment().format('YYYY-MM-DD')}]`,
      agentRmUserId = null,
      engagementAuditAndDisputeType = null,
      pageNumber = 1,
      pageSize = 10,
    } = params;
    
    try {
      set({ qaScoringFeedbackLoading: true, qaScoringFeedbackError: null });
      const response = await getAllQaScoringAndFeedbackForm({
        agentUserId,
        qaUserId,
        monthCount,
        auditedDate,
        agentRmUserId,
        engagementType,
        gender,
        verdict,
        house,
        location,
        pageNumber,
        pageSize,
        engagementAuditAndDisputeType
      });
      set({ qaScoringFeedbackData: response.data });
    } catch (error) {
      set({ qaScoringFeedbackError: error?.message });
      toast.error(_.get(error, ['response', 'data', 'message'], 'Something went wrong'));
    } finally {
      set({ qaScoringFeedbackLoading: false });
    }
  },

  fetchChartAuditData: async (params = {}) => {
    const defaultParams = {
      monthCount: params.monthCount || 1,
    };
    try {
      set({ chartAuditLoading: true, chartAuditError: null });
      const response = await getQaAuditDataForGraphDesign({ ...defaultParams, ...params });
      set({ chartAuditData: response.data });
    } catch (error) {
      set({ chartAuditError: error?.message });
      toast.error(error?.message);
    } finally {
      set({ chartAuditLoading: false });
    }
  },

  assignAgentsToQaFunc: async () => {
    try {
      const response = await assignAgentsToQa();
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error?.message || 'An error occurred while assigning agents to QA');
    }
  },

  fetchLocationViewData: async (params) => {
    try {
      set({ locationViewDataLoading: true });
      const response = await getLocationView(params);
      set({ locationViewData: response.data });
    } catch (error) {
      set({ locationViewDataError: error?.response?.data?.message });
      toast.error(error?.response?.data?.message);
    } finally {
      set({ locationViewDataLoading: false });
    }
  },
  fetchLocationViewFilter: async () => {
    try {
      set({ locationViewDataLoading: true });
      const response = await getLocationViewFilterData();
      set({ locationViewFilterData: response.data });
    } catch (error) {
      set({ locationViewFilterDataError: error?.response?.data?.message });
      toast.error(error?.response?.data?.message);
    } finally {
      set({ locationViewFilterDataLoading: false });
    }
  },

  fetchCoachViewData: async (params) => {
    try {
      set({ coachViewDataLoading: true });
      const response = await getCoachView(params);
      set({ coachViewData: response.data });
    } catch (error) {
      set({ coachViewDataError: error?.response?.data?.message });
      toast.error(error?.response?.data?.message);
    } finally {
      set({ coachViewDataLoading: false });
    }
  },

  fetchCoachViewFilter: async () => {
    try {
      set({ coachViewDataLoading: true });
      const response = await getCoachViewFilterData();
      set({ coachViewFilterData: response.data });
    } catch (error) {
      set({ coachViewFilterDataError: error?.response?.data?.message });
      toast.error(error?.response?.data?.message);
    } finally {
      set({ coachViewFilterDataLoading: false });
    }
  },

  fetchQaViewData: async (params) => {
    try {
      set({ qaViewDataLoading: true });
      const response = await getQaView(params);
      set({ qaViewData: response.data });
    } catch (error) {
      set({ qaViewError: error?.response?.data?.message });
      toast.error(error?.response?.data?.message);
    } finally {
      set({ qaViewDataLoading: false });
    }
  },
  fetchQaViewFilter: async () => {
    try {
      set({ qaViewDataLoading: true });
      const response = await getQaViewFilterData();
      set({ qaViewFilterData: response.data });
    } catch (error) {
      set({ qaViewFilterDataError: error?.response?.data?.message });
      toast.error(error?.response?.data?.message);
    } finally {
      set({ qaViewFilterDataLoading: false });
    }
  },
  fetchApplyDisputes: async (body ,payload) => {
    try {
      const { fetchScoringFormWithRecording } = get()
      set({  disputeApplyDataLoading: true });
      const response = await getDisputeApply(body);
      if (response.status === 200) {
        toast.success('Applied Disputes Successfully');
        let key = payload?.tabkey || null;
        if(isEmpty(key)){
          key = 'questions';
        }
        await fetchScoringFormWithRecording(key,payload?.toggleState,payload?.engagementTrackingId,payload?.agentUserId);
      };
      set({ disputeApplyData: response.data });
    } catch (error) {
      set({ disputeApplyDataError: error?.response?.data?.message });
      toast.error(error?.response?.data?.message);
    } finally {
      set({ disputeApplyDataLoading: false });
    }
  },
  fetchRejectDisputes: async (id ,payload) => {
    try {
      const { fetchScoringFormWithRecording } = get();
      set({  disputeRejectDataLoading: true });
      const response = await getDisputeReject(id);
      if (response.status === 200) {
        toast.success('Rejected Disputes Successfully');
        let key = payload?.tabKey || null;
        if(isEmpty(key)){
          key = 'questions';
        }
        await fetchScoringFormWithRecording(key,payload?.toggleState,payload?.engagementTrackingId,payload?.agentUserId);
      };
      set({  disputeRejecData: response.data });
    } catch (error) {
      set({ disputeRejectDataError: error?.response?.data?.message });
      toast.error(error?.response?.data?.message);
    } finally {
      set({ disputeRejectDataLoading: false });
    }
  },

}));

